// Everything a pilot files is a PDF (Jill 2026-07-26).
//
// SETTLED THE HARD WAY, DO NOT RETRY: Safari does NOT give a web file input
// access to the iOS document scanner. Jill tested iPad mini and iPhone on
// 2026-07-27 (build f9c11d5) and neither offers it. "Scan Documents" is a
// feature of the Files *app*; the picker Safari opens for a file input is a
// restricted browser that does not carry it. Restricting accept to
// application/pdf does not surface a scanner — it just makes Safari skip its
// action sheet and drop you straight into a file browser with nothing to scan
// with, which is the dead-end button this replaced. There is no accept string,
// capture value, or picker trick that reaches the scanner from the web.
//
// So there are two honest ways in, and both end at a PDF:
//
//  1. A photo, converted here to a one-page PDF. This is the path that always
//     works, so it is the primary button everywhere.
//  2. A PDF the pilot already made elsewhere — scanned in Files, Dropbox, or
//     PDF Expert, all of which real pilots have. Passed through untouched.
//
// A true in-portal scanner (live camera, edge detection, deskew, multi-page
// tray) remains possible but is a real build; it is Jill's open call, not an
// oversight.
//
// The redraw through a canvas is load-bearing, not just a size trim: iPhones
// hand over HEIC, which pdf-lib cannot embed at all. Safari CAN decode HEIC, so
// drawing to a canvas and re-encoding as JPEG is what makes an iPhone photo
// embeddable in the first place. Anything the browser cannot decode is passed
// through untouched rather than silently dropped — a receipt we can't convert
// still has to reach the office.

import { makeLazyLoader } from "./lazyImport";
import { shrinkPdfBytes } from "./shrinkPdf";

// pdf-lib is a lazy chunk; over ship wifi the use-time download can drop
// (same failure class as Aaron's Judee export, 2026-08-03) — warm+retry.
// Callers keep their own fallbacks (a photo that can't convert is filed
// as-is), so a final failure here degrades, never dead-ends.
const loadPdfLib = makeLazyLoader(
  () => import("pdf-lib"),
  "The attachment converter couldn't download over this connection."
);

export const PHOTO_ACCEPT = "image/*";
export const PDF_ACCEPT = "application/pdf";
export const ANY_ACCEPT = "image/*,application/pdf";

const MAX_PX = 1600;      // phone photos are 3-5 MB; receipts email in batches
const JPEG_QUALITY = 0.8;
const LETTER = [612, 792];
// Leaves the top strip clear so the caption appendReceiptsToPdf() draws on a
// copied page lands above the image instead of across it.
const TOP_MARGIN = 60;
const SIDE_MARGIN = 36;

export function isPdfFile(file) {
  return (file?.type || "").includes("pdf") || /\.pdf$/i.test(file?.name || "");
}

export function isImageFile(file) {
  return (file?.type || "").startsWith("image/");
}

function pdfName(name) {
  const base = (name || "scan").replace(/\.[A-Za-z0-9]+$/, "");
  return `${base}.pdf`;
}

// Decode -> downscale -> JPEG. Returns null if the browser can't decode it.
async function toJpegBytes(file) {
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, MAX_PX / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bmp.width * scale));
    canvas.height = Math.max(1, Math.round(bmp.height * scale));
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", JPEG_QUALITY));
    if (!blob) return null;
    return new Uint8Array(await blob.arrayBuffer());
  } catch {
    return null;
  }
}

// Count pages so a multi-page scan can be flagged in the viewer (iPad Safari
// renders only page 1 in an iframe). Unknown is fine; it just means no flag.
async function countPages(blob) {
  try {
    const { PDFDocument } = await loadPdfLib();
    return (await PDFDocument.load(await blob.arrayBuffer(), { updateMetadata: false })).getPageCount();
  } catch {
    return null;
  }
}

/**
 * One file in, one PDF out where possible.
 *
 * Returns { blob, name, contentType, pages, converted }.
 *  - PDF in:   passed straight through, page count read.
 *  - image in: converted to a one-page PDF (converted: true).
 *  - neither, or a photo the browser can't decode: passed through as-is with
 *    its original type, so it still gets filed. Callers must not assume PDF.
 */
export async function fileToPdf(file) {
  if (isPdfFile(file)) {
    // An oversize PDF (a 14 MB hotel scan, 2026-09-09) is re-drawn as JPEG
    // pages so the report it rides in stays small enough to email. Under the
    // threshold, or if the redraw fails, the file passes through untouched.
    const { bytes, shrunk, pages } = await shrinkPdfBytes(new Uint8Array(await file.arrayBuffer()));
    const blob = shrunk ? new Blob([bytes], { type: "application/pdf" }) : file;
    return {
      blob,
      name: file.name || "scan.pdf",
      contentType: "application/pdf",
      pages: pages ?? await countPages(file),
      converted: false,
      shrunk,
      originalBytes: file.size,
    };
  }

  if (isImageFile(file)) {
    const jpeg = await toJpegBytes(file);
    if (jpeg) {
      try {
        const { PDFDocument } = await loadPdfLib();
        const doc = await PDFDocument.create();
        await addImagePage(doc, jpeg);
        const bytes = await doc.save();
        return {
          blob: new Blob([bytes], { type: "application/pdf" }),
          name: pdfName(file.name),
          contentType: "application/pdf",
          pages: 1,
          converted: true,
        };
      } catch { /* fall through and file the photo itself */ }
    }
    // Couldn't convert. File the compressed photo if we got one, else the
    // original — never drop it.
    if (jpeg) {
      return {
        blob: new Blob([jpeg], { type: "image/jpeg" }),
        name: file.name || "photo.jpg",
        contentType: "image/jpeg",
        pages: null,
        converted: false,
      };
    }
  }

  return {
    blob: file,
    name: file.name || "attachment",
    contentType: file.type || "application/octet-stream",
    pages: null,
    converted: false,
  };
}

export async function filesToPdfs(files) {
  const out = [];
  for (const f of Array.from(files || [])) out.push(await fileToPdf(f));
  return out;
}

// Draw one image onto its own letter page of an already-open doc, so a single
// page and a merged page land identically.
async function addImagePage(doc, jpeg) {
  const img = await doc.embedJpg(jpeg);
  const page = doc.addPage(LETTER);
  const dims = img.scaleToFit(LETTER[0] - SIDE_MARGIN * 2, LETTER[1] - TOP_MARGIN - SIDE_MARGIN);
  page.drawImage(img, {
    x: (LETTER[0] - dims.width) / 2,
    y: LETTER[1] - TOP_MARGIN - dims.height,
    width: dims.width,
    height: dims.height,
  });
}

/**
 * Several files in, ONE multi-page PDF out, in the order given.
 *
 * This is the multi-page answer for documents that are genuinely one document:
 * a two-sided paper training record, a two-page pilot card. Images become pages
 * and any PDF selected alongside them has its pages copied in, so a pilot can
 * mix a photo of page 1 with a scan of page 2.
 *
 * Deliberately NOT used for expense receipts: three photos there almost always
 * means three separate receipts needing their own label and amount, and merging
 * them would destroy that.
 *
 * Returns { blob, name, contentType, pages, converted } or **null** if any file
 * could not be included. Null means "do not use me" — the caller must fall back
 * to filing each file separately, so a page is never silently dropped on the
 * floor to make a tidy merge.
 */
export async function filesToOnePdf(files, { name = "scan.pdf" } = {}) {
  const list = Array.from(files || []);
  if (!list.length) return null;
  if (list.length === 1) return fileToPdf(list[0]);

  try {
    const { PDFDocument } = await loadPdfLib();
    const doc = await PDFDocument.create();
    for (const f of list) {
      if (isPdfFile(f)) {
        const src = await PDFDocument.load(await f.arrayBuffer(), { ignoreEncryption: true });
        const pages = await doc.copyPages(src, src.getPageIndices());
        pages.forEach((p) => doc.addPage(p));
        continue;
      }
      if (isImageFile(f)) {
        const jpeg = await toJpegBytes(f);
        if (!jpeg) return null;   // undecodable page — abandon the merge, lose nothing
        await addImagePage(doc, jpeg);
        continue;
      }
      return null;               // something we can't page at all
    }
    const count = doc.getPageCount();
    if (!count) return null;
    const bytes = await doc.save();
    return {
      blob: new Blob([bytes], { type: "application/pdf" }),
      name: pdfName(name),
      contentType: "application/pdf",
      pages: count,
      converted: true,
    };
  } catch {
    return null;
  }
}

// Copy for the hint under the capture buttons. Names the apps a pilot actually
// has, and never promises a scanner the browser can't reach.
export const SCAN_HINT =
  "A photo is saved as a PDF for you. Already scanned it in Files, Dropbox, or PDF Expert? Add that PDF instead.";
